import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '../lib/auth'
import { subscribeOpenBlock } from './blocks'
import {
  seedAppSettings,
  seedTaxonomy,
  subscribeAppSettings,
  subscribeIterations,
  subscribeTaxonomy,
  categoryMap,
} from './settings'
import { DEFAULT_ITERATIONS, type IterationConfig } from './iterations'
import { DEFAULT_APP_SETTINGS } from './defaults'
import type { AppSettings, Block, Category, Taxonomy } from './types'

export interface WorkspaceValue {
  uid: string
  taxonomy: Taxonomy | null
  categories: Map<string, Category>
  settings: AppSettings
  iterations: IterationConfig
  openBlock: Block | null
  loading: boolean
  error: string | null
}

export const WorkspaceContext = createContext<WorkspaceValue | null>(null)

export function WorkspaceProvider({ uid, children }: { uid: string; children: ReactNode }) {
  const [taxonomy, setTaxonomy] = useState<Taxonomy | null>(null)
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [openBlock, setOpenBlock] = useState<Block | null>(null)
  const [iterations, setIterations] = useState<IterationConfig>(DEFAULT_ITERATIONS)
  const [loaded, setLoaded] = useState({ taxonomy: false, settings: false })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fail = (cause: Error) => setError(cause.message)

    const stopTaxonomy = subscribeTaxonomy(
      uid,
      ({ value, confirmedMissing }) => {
        setTaxonomy(value)
        setLoaded((prev) => ({ ...prev, taxonomy: true }))
        // Seed only once the server confirms it is absent — never from a cold cache.
        if (confirmedMissing) void seedTaxonomy(uid).catch(fail)
      },
      fail,
    )

    const stopSettings = subscribeAppSettings(
      uid,
      ({ value, confirmedMissing }) => {
        setSettings(value)
        setLoaded((prev) => ({ ...prev, settings: true }))
        if (confirmedMissing) void seedAppSettings(uid).catch(fail)
      },
      fail,
    )

    const stopOpen = subscribeOpenBlock(uid, setOpenBlock, fail)
    const stopIterations = subscribeIterations(uid, setIterations, fail)

    return () => {
      stopTaxonomy()
      stopSettings()
      stopOpen()
      stopIterations()
    }
  }, [uid])

  const value = useMemo<WorkspaceValue>(
    () => ({
      uid,
      taxonomy,
      categories: categoryMap(taxonomy),
      settings: settings ?? DEFAULT_APP_SETTINGS,
      iterations,
      openBlock,
      loading: !loaded.taxonomy || !loaded.settings,
      error,
    }),
    [uid, taxonomy, settings, iterations, openBlock, loaded, error],
  )

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

export function useWorkspace(): WorkspaceValue {
  const value = useContext(WorkspaceContext)
  if (!value) throw new Error('useWorkspace must be used inside <WorkspaceProvider>')
  return value
}

/** Convenience for screens that only need the signed-in uid. */
export function useUid(): string {
  const { uid } = useAuth()
  if (!uid) throw new Error('No signed-in owner')
  return uid
}
